import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { profile } from 'api';
import { RootState } from 'store';

import { deleteUser } from 'actions/user';
import ConfirmationModal from 'components/ConfirmationModal';
import UserList from 'components/UserList';
import { TextModal } from 'components/UserList/styles';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser } from 'models/adminUser';
import { GroupedListModel } from 'models/groupedList';
import { GetAllUsersAndInvitesResponse } from 'models/user';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { htmlT } from 'utils/generic';

interface Props {
  title: string;
  subTitle?: string;
  alreadySelected?: GroupedListModel[];
  isModal?: boolean;
  onConfirmSelection: (users: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
  isApprovalSelection?: boolean;
  selectableType: 'arrow' | 'check' | 'radio' | 'trashcan' | null;
  userExclusion?: AdminUser[];
  buttonLabel?: string;
}

const UserListContainer = ({ userExclusion, ...rest }: Props) => {
  const { t } = useStoreTranslation();
  const [userList, setUserList] = useState<GroupedListModel[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDeleteResult, setShowDeleteResult] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resultConfirmDeleteText, setResultConfirmDeleteText] = useState('');
  const [resultConfirmDeleteSubText, setResultConfirmDeleteSubText] = useState(
    t('userList.confirmDeleteResult.textSuccessSubtitle'),
  );
  const [userToDelete, setUserToDelete] = useState<GroupedListModel>();
  const toast = useToast();
  const [totalUsers, setTotalUsers] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);
  const MAX_ITEMS_PER_PAGE = 10;

  const getData = async (page?: number, userName?: string) => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await profile.post<GetAllUsersAndInvitesResponse>('/Invite/GetAllUsersAndInvites', {
        search: userName ?? '',
        pageSize: MAX_ITEMS_PER_PAGE,
        pageNumber: page ?? 1,
      } as any);

      setCurrentPage(page ?? 1);

      const dataFiltered: GroupedListModel[] = [];

      if (data.totalResults === 0) {
        setUserList([]);
        setLoading(false);
        return;
      }

      for (const user of data.result) {
        const userExistsInExcludeList = userExclusion?.find((userExcluded) => userExcluded.id === user.id);

        if (!userExistsInExcludeList) {
          dataFiltered.push(user);
        }
      }

      setUserList(
        dataFiltered.map((user: GroupedListModel) => ({
          id: user.id,
          name: user.name,
          lastName: user.lastName,
          email: user.email,
          userGroupId: user.userGroupId || '',
          userGroupName: user.userGroupName || '',
          type: user.type || '',
        })),
      );

      if (dataFiltered.length !== data.result.length) {
        setTotalUsers(data.totalResults - (dataFiltered.length - data.result.length));
      } else {
        setTotalUsers(data.totalResults);
      }
    } catch (error) {
      console.error(error);
      toast.add({
        type: 'danger',
        message: t('group.groupAggregation.errorFetch'),
      });
      setLoading(false);
    }
    setLoading(false);
  };

  const handleOnDelete = (users: GroupedListModel[]) => {
    setShowDeleteConfirmation(true);
    setUserToDelete(users[0]);
  };

  const onCloseDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
    setDeleting(false);
  };

  const onDeleteConfirm = async () => {
    if (userToDelete === undefined) {
      setResultConfirmDeleteText(t('userList.confirmDeleteResult.error.title'));
      setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.genericSubText'));
      setShowDeleteResult(true);
      return;
    }

    try {
      setDeleting(true);
      const {
        data: { errors, succeeded },
      } = await deleteUser(userToDelete?.id);

      setDeleting(false);

      if (succeeded) {
        setShowDeleteConfirmation(false);

        setResultConfirmDeleteText(t('userList.confirmDeleteResult.textSuccessTitle'));
        setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.textSuccessSubtitle'));
        setShowDeleteResult(true);
      } else {
        setResultConfirmDeleteText(t('userList.confirmDeleteResult.error.title'));

        if (errors?.indexOf('UserNotFound') !== -1) {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.userNotFound'));
        } else if (errors?.indexOf('LoggedUserNotFound') !== -1) {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.loggedUserNotFound'));
        } else if (errors?.indexOf('LoggedUserIsNotAdmin') !== -1) {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.loggedUserIsNotAdmin'));
        } else if (errors?.indexOf('LoggedCompanyIdIsNotTheSameFromUserCompanyId') !== -1) {
          setResultConfirmDeleteSubText(
            t('userList.confirmDeleteResult.error.loggedCompanyIdIsNotTheSameFromUserCompanyId'),
          );
        } else if (errors?.indexOf('ThereAreOnly1User') !== -1) {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.thereAreOnly1User'));
        } else if (errors?.indexOf('ThereAreOnly1Admin') !== -1) {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.thereAreOnly1Admin'));
        } else {
          setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.genericSubText'));
        }

        setShowDeleteResult(true);
      }

      setShowDeleteConfirmation(false);
    } catch (error) {
      setResultConfirmDeleteText(t('userList.confirmDeleteResult.error.title'));
      setResultConfirmDeleteSubText(t('userList.confirmDeleteResult.error.genericSubText'));
      setShowDeleteResult(true);
    }
  };

  const onResultConfirmCancelClick = () => {
    setShowDeleteResult(false);
    setShowDeleteConfirmation(false);
    setDeleting(false);

    if (getData) {
      getData();
    }
  };

  return (
    <>
      <UserList
        userList={userList}
        isLoading={isLoading}
        {...rest}
        onDelete={(users) => handleOnDelete(users)}
        loadUsers={getData}
        totalUsers={totalUsers}
        currentPage={currentPage}
        maxItemsPerPage={MAX_ITEMS_PER_PAGE}
      />
      <ConfirmationModal
        open={showDeleteConfirmation}
        title={t('userList.confirmDelete.title')}
        onClose={onCloseDeleteConfirmation}
        onConfirm={onDeleteConfirm}
        submitting={deleting}
        showCancelButton={true}
      >
        <TextModal>{htmlT(t, 'userList.confirmDelete.text')}</TextModal>
      </ConfirmationModal>

      <ResponseScreen
        show={showDeleteResult}
        text={resultConfirmDeleteText}
        subText={resultConfirmDeleteSubText}
        buttonText={t('userList.confirmDeleteResult.buttonText')}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.Airplane}
        buttonOnClick={onResultConfirmCancelClick}
      />
    </>
  );
};

export default UserListContainer;
